import { useEffect, useState, useMemo } from 'react';
import { useAsyncCallback } from 'react-async-hook';
import { fetchList } from 'pages/ProjectInsights/services';
import { useCurrentRegion } from 'state/currentRegion';
import { useAddressQuery } from 'state/queryParams';
import { QUERY_PARAM } from 'utils/constants/QueryParameters';
import type { PaginatedProject } from 'types/index';

type ProjectsByAddressProps = {
  overrideAddress?: string;
  clusterId?: string;
};

export const useProjectsByAddress = ({
  overrideAddress,
  clusterId,
}: ProjectsByAddressProps) => {
  const [address] = useAddressQuery();
  const { currentRegion } = useCurrentRegion();
  const getProjectCards = useAsyncCallback(fetchList);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (clusterId && !getProjectCards.loading) {
      getProjectCards.execute('', page, currentRegion, 5, clusterId);
    } else if (overrideAddress && !getProjectCards.loading) {
      getProjectCards.execute(overrideAddress, page, currentRegion);
    } else if (address && !getProjectCards.loading) {
      getProjectCards.execute(address, page, currentRegion);
    } else {
      getProjectCards.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideAddress, address, clusterId, page, currentRegion]);

  useEffect(() => {
    // Create a new instance of URLSearchParams using the current location search
    const searchParams = new URLSearchParams(window.location.search);

    // Update the address query parameter

    if (address) {
      searchParams.set(QUERY_PARAM.address, address);
    }

    // Update the URL with the modified search parameters
    const newQueryString =
      searchParams.toString().length > 0 ? `?${searchParams.toString()}` : '';
    window.history.replaceState({}, '', `${window.location.pathname}${newQueryString}`);
  }, [address]);

  return useMemo(() => {
    return {
      projects: getProjectCards.result?.projects as PaginatedProject[],
      isLoading: getProjectCards.loading,
      error: getProjectCards.error,
      nextPage: getProjectCards.result?.nextPage,
      count: getProjectCards.result?.count,
      setPage,
      page,
    };
  }, [getProjectCards, page]);
};
